<template>
  <div class="sav-viewer" v-if="formData && Object.keys(formData).length">
    <!-- Section 1 -->
    <section class="sav-block">
      <h4 class="sav-h">Section 1. Personal information</h4>
      <div class="sav-grid">
        <div v-for="row in section1Fields" :key="row.k" class="sav-item">
          <span class="sav-lab">{{ row.l }}</span>
          <div class="sav-val">{{ d(s1[row.k]) }}</div>
        </div>
        <div class="sav-item sav-item--wide">
          <span class="sav-lab">PWD</span>
          <div class="sav-val">{{ s1.pwdYesNo || "—" }}{{ s1.pwdNature ? " — " + s1.pwdNature : "" }}</div>
        </div>
      </div>
    </section>

    <!-- Section 2 -->
    <section class="sav-block">
      <h4 class="sav-h">Section 2. Work related information</h4>
      <div class="sav-grid">
        <div v-for="row in section2Fields" :key="row.k" class="sav-item">
          <span class="sav-lab">{{ row.l }}</span>
          <div class="sav-val">{{ d(s2[row.k]) }}</div>
        </div>
      </div>
    </section>

    <!-- Section 3 -->
    <section class="sav-block">
      <h4 class="sav-h">Section 3. Academic &amp; professional qualifications</h4>
      <div class="sav-table-wrap">
        <table class="sav-table">
          <thead>
            <tr>
              <th>Year</th>
              <th>Course</th>
              <th>Institution</th>
              <th>Level / qualification</th>
              <th>Cert. no.</th>
              <th>Acad. / Prof.</th>
            </tr>
          </thead>
          <tbody>
            <template v-if="s3display.length">
              <tr v-for="(r, i) in s3display" :key="'s3v-' + i">
                <td>{{ d(r.year) }}</td>
                <td>{{ d(r.course) }}</td>
                <td>{{ d(r.institution) }}</td>
                <td>{{ d(r.level) }}</td>
                <td>{{ d(r.certNo) }}</td>
                <td>{{ d(r.acadProf) }}</td>
              </tr>
            </template>
            <tr v-else>
              <td colspan="6" class="sav-empty-row">No entries</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <!-- Section 4 -->
    <section class="sav-block">
      <h4 class="sav-h">Section 4. Professional bodies</h4>
      <div class="sav-table-wrap">
        <table class="sav-table">
          <thead>
            <tr>
              <th>Body</th>
              <th>Certification</th>
              <th>Registration / cert. no.</th>
              <th>Validity / expiry</th>
            </tr>
          </thead>
          <tbody>
            <template v-if="s4display.length">
              <tr v-for="(r, i) in s4display" :key="'s4v-' + i">
                <td>{{ d(r.body) }}</td>
                <td>{{ d(r.certification) }}</td>
                <td>{{ d(r.regNo) }}</td>
                <td>{{ d(r.validity) }}</td>
              </tr>
            </template>
            <tr v-else>
              <td colspan="4" class="sav-empty-row">No entries</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <!-- Section 5 -->
    <section class="sav-block">
      <h4 class="sav-h">Section 5. Other trainings (last 5 years)</h4>
      <div class="sav-table-wrap">
        <table class="sav-table">
          <thead>
            <tr>
              <th>Period</th>
              <th>Institution</th>
              <th>Sponsorship</th>
              <th>Course</th>
              <th>Qualification</th>
            </tr>
          </thead>
          <tbody>
            <template v-if="s5display.length">
              <tr v-for="(r, i) in s5display" :key="'s5v-' + i">
                <td>{{ d(r.period) }}</td>
                <td>{{ d(r.institution) }}</td>
                <td>{{ d(r.sponsorship) }}</td>
                <td>{{ d(r.course) }}</td>
                <td>{{ d(r.qualification) }}</td>
              </tr>
            </template>
            <tr v-else>
              <td colspan="5" class="sav-empty-row">No entries</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <!-- Section 6 -->
    <section class="sav-block">
      <h4 class="sav-h">Section 6. Training needs (priority)</h4>
      <div class="sav-table-wrap">
        <table class="sav-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Course / programme</th>
              <th>Proposed institution</th>
            </tr>
          </thead>
          <tbody>
            <template v-if="s6display.length">
              <tr v-for="(r, i) in s6display" :key="'s6v-' + i">
                <td>{{ i + 1 }}</td>
                <td>{{ d(r.course) }}</td>
                <td>{{ d(r.institution) }}</td>
              </tr>
            </template>
            <tr v-else>
              <td colspan="3" class="sav-empty-row">No entries</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <!-- Section 7 -->
    <section class="sav-block">
      <h4 class="sav-h">Section 7. Work experience</h4>
      <div class="sav-text-block">
        <span class="sav-lab">A) Current duties</span>
        <div class="sav-multi">{{ dMultiline(s7.currentDuties) }}</div>
      </div>
      <div class="sav-text-block">
        <span class="sav-lab">B) Duties to delete from schedule</span>
        <div class="sav-multi">{{ dMultiline(s7.dutiesToDelete) }}</div>
      </div>
      <div class="sav-table-wrap sav-mt">
        <span class="sav-lab sav-lab--block">C) Previous experience</span>
        <table class="sav-table">
          <thead>
            <tr>
              <th>From</th>
              <th>To</th>
              <th>Institution</th>
              <th>Position</th>
            </tr>
          </thead>
          <tbody>
            <template v-if="s7prevDisplay.length">
              <tr v-for="(r, i) in s7prevDisplay" :key="'s7v-' + i">
                <td>{{ d(r.from) }}</td>
                <td>{{ d(r.to) }}</td>
                <td>{{ d(r.institution) }}</td>
                <td>{{ d(r.position) }}</td>
              </tr>
            </template>
            <tr v-else>
              <td colspan="4" class="sav-empty-row">No entries</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <!-- Section 8 -->
    <section class="sav-block">
      <h4 class="sav-h">Section 8. Skills information</h4>
      <p class="sav-sub">Job-specific / hard skills</p>
      <div class="sav-grid">
        <div class="sav-item sav-item--wide">
          <span class="sav-lab">Administrative</span>
          <div class="sav-multi">{{ dMultiline(hard.administrative) }}</div>
        </div>
        <div class="sav-item sav-item--wide">
          <span class="sav-lab">Managerial</span>
          <div class="sav-multi">{{ dMultiline(hard.managerial) }}</div>
        </div>
        <div class="sav-item sav-item--wide">
          <span class="sav-lab">Technical</span>
          <div class="sav-multi">{{ dMultiline(hard.technical) }}</div>
        </div>
        <div class="sav-item sav-item--wide">
          <span class="sav-lab">RPL</span>
          <div class="sav-multi">{{ dMultiline(hard.rpl) }}</div>
        </div>
      </div>
      <div class="sav-text-block sav-mt">
        <span class="sav-lab">Soft skills</span>
        <div class="sav-multi">{{ dMultiline(s8.softSkills) }}</div>
      </div>
    </section>

    <!-- Section 9 -->
    <section class="sav-block">
      <h4 class="sav-h">Section 9. Additional information</h4>
      <div v-for="(q, idx) in section9Qs" :key="'s9-' + idx" class="sav-text-block">
        <span class="sav-lab">{{ q.l }}</span>
        <div class="sav-multi">{{ dMultiline(s9[q.k]) }}</div>
      </div>
    </section>

    <!-- Declaration -->
    <section class="sav-block sav-block--last">
      <h4 class="sav-h">Declaration</h4>
      <div class="sav-grid">
        <div class="sav-item">
          <span class="sav-lab">Full name</span>
          <div class="sav-val">{{ d(decl.fullName) }}</div>
        </div>
        <div class="sav-item">
          <span class="sav-lab">ID No.</span>
          <div class="sav-val">{{ d(decl.idNo) }}</div>
        </div>
      </div>
      <p class="sav-footnote">Supporting documents as required by HR policy should be on file.</p>
    </section>
  </div>
  <div v-else class="sav-empty">No assessment form data.</div>
</template>

<script>
export default {
  name: "SkillsAuditAssessmentViewer",
  props: {
    form: { type: Object, default: null }
  },
  data() {
    return {
      section1Fields: [
        { k: "surname", l: "Surname" },
        { k: "middleName", l: "Middle name" },
        { k: "otherNames", l: "Other names" },
        { k: "idNo", l: "ID No." },
        { k: "dateOfBirth", l: "Date of birth" },
        { k: "gender", l: "Gender" },
        { k: "phoneNo", l: "Phone" },
        { k: "kraPin", l: "KRA PIN" },
        { k: "ethnicity", l: "Ethnicity" },
        { k: "homeCounty", l: "Home county" },
        { k: "subCounty", l: "Sub-county" },
        { k: "ward", l: "Ward" },
        { k: "ncpwdCertNo", l: "NCPWD cert. no." },
        { k: "kraExemptionCertNo", l: "KRA exemption cert. no." },
        { k: "nokName", l: "Next of kin" },
        { k: "nokRelationship", l: "Relationship" },
        { k: "nokPhone", l: "NOK phone" },
        { k: "altNokName", l: "Alternative NOK" },
        { k: "altNokRelationship", l: "Relationship" },
        { k: "altNokPhone", l: "Phone" }
      ],
      section2Fields: [
        { k: "employeeNumber", l: "Employee number" },
        { k: "payGroup", l: "Pay group" },
        { k: "designationPayroll", l: "Designation (payroll)" },
        { k: "currentDeployment", l: "Current deployment" },
        { k: "dateFirstAppointment", l: "Date of 1st appointment" },
        { k: "jobGroupFirst", l: "Job group at 1st appointment" },
        { k: "dateCurrentAppointment", l: "Date current appointment / promotion" },
        { k: "currentJobGroup", l: "Current job group" },
        { k: "departmentAgency", l: "Department / agency" },
        { k: "directorate", l: "Directorate / section / division" },
        { k: "workStation", l: "Work station" },
        { k: "yearsCurrentStation", l: "Years in current duty station" },
        { k: "yearsPublicService", l: "Years in public service" },
        { k: "retirementOrContractEnd", l: "Retirement / end of contract" }
      ],
      section9Qs: [
        { k: "q1", l: "1) Major roles / core mandate of your directorate" },
        { k: "q2", l: "2) How your job contributes to the mandate" },
        { k: "q3", l: "3) Other roles you would be interested in" },
        { k: "q4", l: "4) Challenges hindering performance" },
        { k: "q5", l: "5) Other relevant information" }
      ]
    };
  },
  computed: {
    formData() {
      const f = this.form;
      if (f == null) return null;
      if (typeof f === "string") {
        try {
          const o = JSON.parse(f);
          return o && typeof o === "object" ? o : null;
        } catch (_) {
          return null;
        }
      }
      return typeof f === "object" ? f : null;
    },
    s1() {
      return this.formData?.section1 || {};
    },
    s2() {
      return this.formData?.section2 || {};
    },
    s3rows() {
      const a = this.formData?.section3;
      return Array.isArray(a) ? a : [];
    },
    s4rows() {
      const a = this.formData?.section4;
      return Array.isArray(a) ? a : [];
    },
    s5rows() {
      const a = this.formData?.section5;
      return Array.isArray(a) ? a : [];
    },
    s6rows() {
      const a = this.formData?.section6;
      return Array.isArray(a) ? a : [];
    },
    s3display() {
      return this.filterNonEmptyRows(this.s3rows, ["year", "course", "institution", "level", "certNo", "acadProf"]);
    },
    s4display() {
      return this.filterNonEmptyRows(this.s4rows, ["body", "certification", "regNo", "validity"]);
    },
    s5display() {
      return this.filterNonEmptyRows(this.s5rows, ["period", "institution", "sponsorship", "course", "qualification"]);
    },
    s6display() {
      return this.filterNonEmptyRows(this.s6rows, ["course", "institution"]);
    },
    s7() {
      return this.formData?.section7 || { currentDuties: "", dutiesToDelete: "", previous: [] };
    },
    s7prev() {
      const p = this.s7.previous;
      return Array.isArray(p) ? p : [];
    },
    s7prevDisplay() {
      return this.filterNonEmptyRows(this.s7prev, ["from", "to", "institution", "position"]);
    },
    s8() {
      return this.formData?.section8 || { hard: {}, softSkills: "" };
    },
    hard() {
      const h = this.s8.hard || {};
      return {
        administrative: h.administrative,
        managerial: h.managerial,
        technical: h.technical,
        rpl: h.rpl
      };
    },
    s9() {
      return this.formData?.section9 || {};
    },
    decl() {
      return this.formData?.declaration || {};
    }
  },
  methods: {
    filterNonEmptyRows(rows, keys) {
      if (!Array.isArray(rows)) return [];
      return rows.filter((r) =>
        keys.some((k) => String(r[k] ?? "").trim() !== "")
      );
    },
    d(v) {
      if (v === null || v === undefined || v === "") return "—";
      return String(v);
    },
    dMultiline(v) {
      const s = this.d(v);
      return s === "—" ? "—" : s;
    }
  }
};
</script>

<style scoped>
.sav-viewer {
  font-size: 13px;
  color: #334155;
}

.sav-block {
  margin-bottom: 22px;
  padding-bottom: 18px;
  border-bottom: 1px solid #e2e8f0;
}

.sav-block--last {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.sav-h {
  margin: 0 0 14px;
  font-size: 15px;
  font-weight: 700;
  color: #0d4f73;
  letter-spacing: -0.02em;
}

.sav-sub {
  margin: 0 0 10px;
  font-size: 12px;
  color: #64748b;
}

.sav-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 12px 18px;
}

.sav-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.sav-item--wide {
  grid-column: 1 / -1;
}

.sav-lab {
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.sav-lab--block {
  display: block;
  margin-bottom: 8px;
}

.sav-val {
  font-weight: 500;
  color: #0f172a;
  word-break: break-word;
}

.sav-multi {
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.5;
  padding: 10px 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  min-height: 2.5em;
}

.sav-text-block {
  margin-bottom: 14px;
}

.sav-text-block .sav-lab {
  display: block;
  margin-bottom: 6px;
}

.sav-mt {
  margin-top: 14px;
}

.sav-table-wrap {
  overflow-x: auto;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
}

.sav-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.sav-table th {
  text-align: left;
  padding: 10px 12px;
  background: #f1f5f9;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #475569;
  border-bottom: 1px solid #e2e8f0;
}

.sav-table td {
  padding: 10px 12px;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: top;
}

.sav-table tbody tr:last-child td {
  border-bottom: none;
}

.sav-footnote {
  margin: 14px 0 0;
  font-size: 12px;
  color: #64748b;
}

.sav-empty {
  padding: 24px;
  text-align: center;
  color: #94a3b8;
  font-size: 14px;
}

.sav-empty-row {
  text-align: center;
  color: #94a3b8;
  font-style: italic;
}
</style>
